// One Smart import batch of a household (#2334), opened from the family card's Imports tab: every recipe it asked for,
// what became of it, the reason the app showed and the error behind it, and a failed import's text while it is kept
// (30 days). adminSmartImport's `batch` and `failure_text` read them through the Admin SDK; the drawer markup is in
// templates/users.html. The *HTML functions are pure, so tests/admin/familyImportBatch.test.js can check them.

import { BADGE, BUTTON, SEGMENT, TABLE } from "./ui/classes.js";
import { solidIcon } from "./ui/icons.js";
import { escapeHTML, IMPORT_BADGES, safeUrl } from "./familyDetailsPanel.js";
import { showDialog } from "./ui/dialog.js";

const MUTED = "text-sm text-gray-500 dark:text-gray-400";
const MONO = "font-mono text-xs text-gray-500 dark:text-gray-400";
const TD = "px-3 py-2 text-sm text-gray-500 dark:text-gray-400 align-top";

export const BATCH_FILTERS = [
  { id: "all", label: "All" },
  { id: "failed", label: "Failed" },
  { id: "open", label: "Not finished" }
];
const ENDED = new Set(["completed", "failed"]);
// A Mealime recipe link in any of its forms (r.mealime.com/<id>, /recipes/<slug>/<id>, the print view), as the server reads them
const MEALIME_LINK = /^https?:\/\/(?:[a-z]+\.)?mealime\.com\/(?:\d+|recipes\/[^/]+\/\d+|recipe_variants\/\d+)/i;

/** The failed imports `retry_failed` can import again: those with a Mealime link. The server skips any already in the library. */
export function retryableImports(imports) {
  return (imports || []).filter((imp) => imp.status === "failed" && MEALIME_LINK.test(imp.url || ""));
}

const FORMAT_LABELS = { paprika: "Paprika", url: "Link", text: "Text", photo: "Photo", mealime: "Mealime" };

function formatDate(iso) {
  if (!iso) return "—";
  const d = new Date(iso);
  if (isNaN(d.getTime())) return String(iso);
  return d.toLocaleString(undefined, { dateStyle: "medium", timeStyle: "short" });
}

export function filterImports(imports, filter) {
  if (filter === "failed") return imports.filter((imp) => imp.status === "failed");
  if (filter === "open") return imports.filter((imp) => !ENDED.has(imp.status));
  return imports;
}

/** How many of each status, for the counts above the list. */
export function statusCounts(imports) {
  const counts = {};
  imports.forEach((imp) => { counts[imp.status || "unknown"] = (counts[imp.status || "unknown"] || 0) + 1; });
  return counts;
}

/** The commonest causes among the failed ones, so 50 alike read as one line. */
export function failureCauses(imports) {
  const causes = new Map();
  imports.filter((imp) => imp.status === "failed").forEach((imp) => {
    const cause = imp.error_message || imp.failure_reason || "No reason recorded";
    causes.set(cause, (causes.get(cause) || 0) + 1);
  });
  return [...causes.entries()].sort((a, b) => b[1] - a[1]).map(([cause, count]) => ({ cause, count }));
}

/** What was asked for: its status, title or link, format and when, in one cell so a phone has room for the result. */
function requestHTML(imp) {
  const url = safeUrl(imp.url);
  const title = imp.title || imp.recipe_title;
  const format = FORMAT_LABELS[imp.format] || imp.format;
  const badge = IMPORT_BADGES[imp.status] || `<span class="${BADGE.gray}">${escapeHTML(imp.status || "Unknown")}</span>`;
  const link = url ? `<a href="${escapeHTML(url)}" target="_blank" rel="noopener noreferrer" class="${BUTTON.link} break-all">${escapeHTML(url)}</a>` : "";
  const head = title ? `<div class="text-gray-900 dark:text-white">${escapeHTML(title)}</div>` : "";
  const fallback = !title && !url ? escapeHTML(imp.kind === "text" ? "Pasted text" : "—") : "";
  const meta = [format, formatDate(imp.created_at)].filter(Boolean).map(escapeHTML).join(" · ");
  return `<div class="mb-1">${badge}</div>${head}${link ? `<div class="${title ? "mt-0.5 " : ""}">${link}</div>` : ""}${fallback}<div class="mt-0.5 text-xs">${meta}</div>`;
}

function outcomeHTML(imp, texts) {
  if (imp.status === "completed") {
    return `${escapeHTML(imp.recipe_title || "Added")}${imp.served_from_cache ? ` <span class="${BADGE.gray}">From the cache</span>` : ""}`;
  }
  if (imp.status !== "failed") return imp.stage ? `<span class="text-xs">${escapeHTML(imp.stage)}</span>` : "—";
  const cause = imp.error_message && imp.error_message !== imp.failure_reason
    ? `<div class="mt-1 ${MONO} break-words whitespace-pre-wrap">${escapeHTML(imp.error_message)}</div>`
    : "";
  const text = texts[imp.import_id]
    ? failureTextHTML(texts[imp.import_id])
    : imp.has_failure_text
    ? `<div class="mt-2"><button type="button" data-batch-text="${escapeHTML(imp.import_id)}" class="${BUTTON.small}">${solidIcon("document-text", "size-4 text-gray-400")}Show the text</button></div><div data-batch-text-for="${escapeHTML(imp.import_id)}"></div>`
    : `<div class="mt-1 text-xs">${imp.kind === "text" ? "Text not kept: it failed before failed texts were kept, or over 30 days ago." : ""}</div>`;
  return `<span class="text-red-700 dark:text-red-400">${escapeHTML(imp.failure_reason || "No reason recorded")}</span>${cause}${text}`;
}

/** The drawer's body for what `batch` returned, filtered; `texts` holds the failed texts already read, by import id. */
export function importBatchHTML(result, filter = "all", texts = {}) {
  const batch = result?.batch || {};
  const imports = result?.imports || [];
  const counts = statusCounts(imports);
  const summary = [
    `${imports.length} import${imports.length === 1 ? "" : "s"}${batch.total !== null && batch.total !== undefined && batch.total !== imports.length ? ` (the batch counts ${batch.total})` : ""}`,
    ...Object.entries(counts).map(([status, n]) => `${n} ${escapeHTML(status.replace(/_/g, " "))}`)
  ].join(" · ");
  const causes = failureCauses(imports);
  const causesHTML = causes.length
    ? `<h3 class="mt-6 text-sm font-semibold text-gray-900 dark:text-white">Why they failed</h3>
       <ul class="mt-2 space-y-1">${causes.map(({ cause, count }) => `<li class="text-sm"><span class="font-semibold text-gray-900 dark:text-white">${count} ×</span> <span class="${MONO} break-words">${escapeHTML(cause)}</span></li>`).join("")}</ul>`
    : "";
  const filters = `<div class="${SEGMENT.group}" role="group" aria-label="Show">${BATCH_FILTERS.map((f) => `<button type="button" data-batch-filter="${f.id}" aria-pressed="${f.id === filter}" class="${SEGMENT.button}">${f.label}</button>`).join("")}</div>`;
  const retryable = retryableImports(imports).length;
  const retry = retryable
    ? `<div class="mt-6"><button type="button" data-batch-retry class="${BUTTON.small}">${solidIcon("arrow-path", "size-4 text-gray-400")}Retry ${retryable} failed Mealime recipe${retryable === 1 ? "" : "s"}</button>
       <p class="mt-1 text-xs">Imports each again from its Mealime link, in a new batch for the member who asked; one already in their library is left out. A recipe the cache holds is served at once.</p></div>`
    : "";
  const shown = filterImports(imports, filter);
  const rows = shown.map((imp) => `
      <tr>
        <td class="${TD} w-2/5 max-w-xs">${requestHTML(imp)}</td>
        <td class="${TD} max-w-md">${outcomeHTML(imp, texts)}<div class="mt-1 ${MONO}">${escapeHTML(imp.import_id)}</div></td>
      </tr>`).join("");
  const table = shown.length
    ? `<div class="relative mt-3 overflow-x-auto"><table class="${TABLE.table}"><thead><tr>${["Recipe", "Result"].map((h) => `<th scope="col" class="${TABLE.th}">${h}</th>`).join("")}</tr></thead><tbody class="${TABLE.tbody}">${rows}</tbody></table></div>`
    : `<p class="mt-3 ${MUTED}">None.</p>`;
  return `
    <p class="${MUTED}">Started ${escapeHTML(formatDate(batch.created_at))}${batch.source === "crm" ? " from the CRM" : " from the app"}. ${summary}.</p>
    ${causesHTML}
    ${retry}
    <div class="mt-6">${filters}</div>
    ${table}`;
}

/** A failed import's kept text, or why there is none. */
export function failureTextHTML(result) {
  if (!result?.text) return `<p class="mt-2 ${MUTED}">The text is no longer kept.</p>`;
  return `
    <pre class="mt-2 max-h-80 overflow-auto rounded-md bg-gray-50 p-3 font-mono text-xs whitespace-pre-wrap break-words text-gray-800 dark:bg-white/5 dark:text-gray-200">${escapeHTML(result.text)}</pre>
    <p class="mt-1 text-xs">Kept until ${escapeHTML(formatDate(result.expires_at))}.</p>`;
}

let ctx = null;
let boundBody = null;
let current = null;

const $ = (id) => document.getElementById(id);

/** Binds the drawer once, when a screen with a family card mounts. Reads nothing (platform/CLAUDE.md). */
export function bindFamilyImportBatchDrawer(context) {
  ctx = context;
  const body = $("familyImportBatchBody");
  // The Users and Families screens both bind it; the drawer is in the page once.
  if (!body || body === boundBody) return;
  boundBody = body;
  body.addEventListener("click", (e) => {
    const filter = e.target.closest("[data-batch-filter]");
    if (filter && current?.result) {
      current.filter = filter.dataset.batchFilter;
      body.innerHTML = importBatchHTML(current.result, current.filter, current.texts);
      return;
    }
    const show = e.target.closest("[data-batch-text]");
    if (show) showFailureText(body, show);
    const retry = e.target.closest("[data-batch-retry]");
    if (retry) retryFailed(retry);
  });
}

/** What `retry_failed` did, as one line for the toast. */
export function retrySummary(answer) {
  const r = answer?.results || {};
  const s = answer?.skipped || {};
  const done = [r.ready && `${r.ready} added from the cache`, r.queued && `${r.queued} queued`, r.failed && `${r.failed} failed again`, r.fetch_on_device && `${r.fetch_on_device} waiting for a phone`].filter(Boolean);
  const left = [s.in_library && `${s.in_library} already in the library`, s.no_mealime_link && `${s.no_mealime_link} without a Mealime link`, s.repeated && `${s.repeated} repeated`].filter(Boolean);
  if (!answer?.retried) return `Nothing to retry${left.length ? `: ${left.join(", ")}` : ""}.`;
  return `Retried ${answer.retried}: ${done.join(", ") || "sent"}${left.length ? `. Left out: ${left.join(", ")}` : ""}.`;
}

async function retryFailed(button) {
  if (!current?.result) return;
  const n = retryableImports(current.result.imports).length;
  if (!window.confirm(`Import these ${n} failed Mealime recipes again, from their links, for the member who asked? Those already in their library are left out. No push is sent.`)) return;
  const opened = current;
  button.disabled = true;
  try {
    const { functions, httpsCallable } = ctx;
    const res = await httpsCallable(functions, "adminSmartImport")({ action: "retry_failed", family_id: opened.familyId, batch_id: opened.batchId });
    ctx.showToast?.(retrySummary(res.data), res.data?.retried ? "success" : "info");
    // The new batch, to follow it; the household's card shows it at its next read
    if (res.data?.batch_id && current === opened) openFamilyImportBatch({ familyId: opened.familyId, batchId: res.data.batch_id });
    else button.disabled = false;
  } catch (err) {
    console.error("❌ adminSmartImport retry_failed failed:", err);
    ctx.showToast?.(`Couldn't retry them: ${err.message}`, "error");
    button.disabled = false;
  }
}

async function showFailureText(body, button) {
  if (!current) return;
  const importId = button.dataset.batchText;
  const target = [...body.querySelectorAll("[data-batch-text-for]")].find((el) => el.dataset.batchTextFor === importId);
  button.disabled = true;
  try {
    const { functions, httpsCallable } = ctx;
    const res = await httpsCallable(functions, "adminSmartImport")({ action: "failure_text", family_id: current.familyId, import_id: importId });
    current.texts[importId] = res.data;
    if (target) target.innerHTML = failureTextHTML(res.data);
    button.remove();
  } catch (err) {
    console.error("❌ adminSmartImport failure_text failed:", err);
    ctx.showToast?.(`Couldn't read the text: ${err.message}`, "error");
    button.disabled = false;
  }
}

/** Opens the drawer on one of the household's batches and reads it. */
export async function openFamilyImportBatch({ familyId, batchId }) {
  const body = $("familyImportBatchBody");
  if (!ctx || !body) return;
  const opened = { familyId, batchId, result: null, filter: "all", texts: {} };
  current = opened;
  $("familyImportBatchSubtitle").textContent = batchId;
  body.innerHTML = `<p class="${MUTED}">Reading the batch…</p>`;
  showDialog("familyImportBatchDrawer");
  try {
    const { functions, httpsCallable } = ctx;
    const res = await httpsCallable(functions, "adminSmartImport")({ action: "batch", family_id: familyId, batch_id: batchId });
    if (current !== opened) return;
    opened.result = res.data;
    // A batch with failures opens on them: that is what it is opened for
    if ((res.data?.batch?.failed || 0) > 0) opened.filter = "failed";
    body.innerHTML = importBatchHTML(res.data, opened.filter, opened.texts);
  } catch (err) {
    console.error("❌ adminSmartImport batch failed:", err);
    if (current !== opened) return;
    body.innerHTML = `<p class="text-sm text-red-700 dark:text-red-400">Could not read the batch: ${escapeHTML(err.message || String(err))}</p>`;
  }
}
